/**
 * Billing page wrapper - renders the BillingSettings component with
 * proper layout chrome (breadcrumb, header).
 *
 * Route: /admin/billing
 *
 * Access: requires `organization:view_billing`. The sidebar already hides
 * the menu entry for users without the permission; this page is the
 * defense-in-depth gate for direct URL access.
 *
 * @module components/admin/BillingPage
 */
import React from 'react';
import { AdminBreadcrumb } from './AdminBreadcrumb';
import { BillingSettings } from './BillingSettings';
import { useOrg } from '../../lib/OrgProvider';

/**
 * Billing page with breadcrumb and BillingSettings component.
 * Uses the active organization from OrgProvider context.
 */
export const BillingPage: React.FC = () => {
  const { activeOrg, isLoading, hasPermission } = useOrg();

  if (isLoading || !activeOrg) {
    return (
      <div className="pt-16 md:pt-0 flex items-center justify-center min-h-[200px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-foreground" />
      </div>
    );
  }

  // Hard gate - a user who navigates here directly without the permission
  // must be turned away. RLS on org_billing would deny the underlying
  // reads anyway; this is the UX that explains why.
  if (!hasPermission('organization:view_billing')) {
    return (
      <div className="pt-16 md:pt-0">
        <div className="container mx-auto px-4 py-12">
          <div className="max-w-md mx-auto text-center">
            <h1 className="text-xl font-bold text-foreground mb-2">Billing unavailable</h1>
            <p className="text-sm text-muted-foreground">
              You don't have access to billing for this organization. Ask an
              owner or admin if you need to view or change the plan.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-16 md:pt-0">
      {/* Sticky header bar. activeOrg.name is surfaced here so a user managing
          multiple orgs can never confuse which plan they're about to change -
          this page launches Stripe actions and must not be ambiguous. */}
      <div className="border-b border-border bg-background/95 backdrop-blur-sm sticky top-0 z-10">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-baseline gap-3 min-h-[36px] flex-wrap">
            <h1 className="text-xl font-bold text-foreground">Billing</h1>
            <span className="text-sm text-muted-foreground">
              for <span className="text-foreground font-medium">{activeOrg.name}</span>
            </span>
          </div>
          <AdminBreadcrumb items={[
            { label: 'Settings', href: '/admin/settings' },
            { label: 'Billing' },
          ]} />
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto">
        <BillingSettings orgId={activeOrg.id} />
      </div>
    </div>
  );
};
